import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { graphqlSubscribe } from '../services/graphql';

export interface SOSAlert {
  alertId?: string;
  tripId?: string;
  triggeredBy?: string;
  userName?: string;
  userType?: string;
  latitude?: number;
  longitude?: number;
  locationUrl?: string;
  message?: string;
  timestamp: string;
}

interface SOSContextValue {
  alerts: SOSAlert[];
  unreadCount: number;
  clearUnread: () => void;
}

const SOSContext = createContext<SOSContextValue>({ alerts: [], unreadCount: 0, clearUnread: () => {} });

export function SOSProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<SOSAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const subRef = useRef<{ unsubscribe: () => void } | null>(null);

  useEffect(() => {
    const sub = graphqlSubscribe(
      `subscription OnAdminEvent { onAdminEvent { eventType referenceId sourceModule timestamp additionalPayload } }`,
      {},
      (data: any) => {
        const event = data?.onAdminEvent;
        if (event?.eventType === 'SOS_ALERT') {
          const payload = event.additionalPayload ? JSON.parse(event.additionalPayload) : {};
          const alert: SOSAlert = {
            ...payload,
            tripId: event.referenceId,
            timestamp: event.timestamp,
          };
          setAlerts(prev => [alert, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + 1);
        }
      }
    );
    subRef.current = sub;
    return () => sub?.unsubscribe();
  }, []);

  const clearUnread = () => setUnreadCount(0);

  return (
    <SOSContext.Provider value={{ alerts, unreadCount, clearUnread }}>
      {children}
    </SOSContext.Provider>
  );
}

export const useSOS = () => useContext(SOSContext);
